import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFormRender } from "../../hooks/useFormRender";
import { getMcaEntityFormSchema } from "./mcaEntityFormRegistry";
import { buildResubmitPayload } from "./mcaPayloadHelpers";
import { withDefaultIdIssueCountry } from "../../helpers/clientIdIssueCountry";

const McaResubmitFormModal = ({
  opened,
  onClose,
  request,
  process,
  initialData,
  onSave,
}) => {
  const { t } = useTranslation();
  const entityKey = request?.targetEntity;
  const action = request?.action;
  const openedSessionRef = useRef(false);

  const formSchema = useMemo(() => {
    const schema = getMcaEntityFormSchema(entityKey, t, {
      canChangeClientStatus: true,
    });
    if (!schema) return null;
    return {
      ...schema,
      title: schema.title ?? `Revise ${entityKey}`,
    };
  }, [entityKey, t]);

  const onSubmit = async ({ values }) => {
    const payload = buildResubmitPayload({
      action,
      originalData: request?.originalData,
      formValues: values,
      watchFields: process?.triggerConfig?.watchFields ?? [],
    });
    onSave(payload);
    formAction.close();
  };

  const [renderForm, formAction, formStatus] = useFormRender(
    formSchema ?? {},
    null,
    onSubmit
  );

  useEffect(() => {
    if (!opened || !formSchema || !initialData) return;
    const mode = action === "CREATE" ? "add" : "edit";
    const data =
      entityKey === "client"
        ? withDefaultIdIssueCountry(initialData)
        : initialData;
    formAction.open({
      mode,
      id: request?.targetEntityId ?? data?._id,
      data,
    });
  }, [
    opened,
    formSchema,
    initialData,
    action,
    entityKey,
    request?.targetEntityId,
  ]);

  useEffect(() => {
    if (!opened) {
      formAction.close(false);
      openedSessionRef.current = false;
      return;
    }
    if (formStatus.isOpen) {
      openedSessionRef.current = true;
      return;
    }
    if (openedSessionRef.current) {
      openedSessionRef.current = false;
      onClose();
    }
  }, [opened, formStatus.isOpen, onClose]);

  if (!formSchema) return null;

  return renderForm();
};

export default McaResubmitFormModal;
